// TensorMaps Web Client Configuration

// Runtime config injection: a deployed (k8s/docker) image can override the
// build-time VITE_* values by serving /config.js, which sets window.__APP_CONFIG__.
// This lets a single static image be configured per environment via a k8s ConfigMap.
// Resolution order: runtime (__APP_CONFIG__) -> build-time (import.meta.env) -> default.
type RuntimeConfig = Record<string, string | undefined>;
const runtimeConfig: RuntimeConfig =
  (typeof window !== 'undefined' &&
    (window as Window & { __APP_CONFIG__?: RuntimeConfig }).__APP_CONFIG__) ||
  {};
const env = (key: string): string | undefined => {
  const runtimeValue = runtimeConfig[key];
  // Treat empty string as "unset" so a blank runtime value falls back to build-time/default.
  if (runtimeValue !== undefined && runtimeValue !== '') return runtimeValue;
  return (import.meta.env as Record<string, string | undefined>)[key];
};

// A browser WebSocket needs an ABSOLUTE ws(s):// URL — it can't use a relative
// path the way fetch can, and a hardcoded host won't work across self-host
// domains. When the API is served same-origin behind the reverse proxy
// (VITE_API_BASE_URL is a relative path), derive the socket origin from the page
// so GeoChat connects on tensormaps.com or any customer domain with no per-deploy
// config — and uses wss:// on HTTPS (a ws:// URL would be mixed-content-blocked).
function resolveWebsocketUrl(): string {
  const explicit = env('VITE_WEBSOCKET_URL');
  if (explicit) return explicit;
  const apiBase = env('VITE_API_BASE_URL') || '';
  if (typeof window !== 'undefined' && window.location && apiBase.startsWith('/')) {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}${apiBase}`;
  }
  return 'ws://localhost:8004';
}

export const config = {
  // API Configuration
  api: {
    baseUrl: env('VITE_API_BASE_URL') || 'http://localhost:8000/api/v1',
    tileServerUrl: env('VITE_TILE_SERVER_URL') || 'http://localhost:8080',
    websocketUrl: resolveWebsocketUrl(),
    timeout: 30000,
    retryAttempts: 3,
  },

  // Map Configuration - Default to India (Hyderabad)
  map: {
    defaultCenter: {
      lat: parseFloat(env('VITE_DEFAULT_CENTER_LAT') || '17.385'),
      lng: parseFloat(env('VITE_DEFAULT_CENTER_LNG') || '78.4867'),
    },
    defaultZoom: parseInt(env('VITE_DEFAULT_ZOOM') || '12', 10),
    minZoom: parseInt(env('VITE_MIN_ZOOM') || '1', 10),
    maxZoom: parseInt(env('VITE_MAX_ZOOM') || '20', 10),
    // Single source of truth for base-map style URLs. Both the initial style
    // (MapContainer) and the style switcher (mapStore.getStyleUrl) resolve from
    // here so they never drift apart. `satellite` is empty unless a real aerial
    // style is configured — when blank, the UI hides the Satellite option instead
    // of silently falling back to the default street style.
    styles: {
      default: env('VITE_STYLE_DEFAULT') || 'http://localhost:8080/styles/bright/style.json',
      dark: env('VITE_STYLE_DARK') || 'http://localhost:8080/styles/dark/style.json',
      satellite: env('VITE_STYLE_SATELLITE') || '',
    },
  },

  // Preset regions for major Indian cities (offline maps)
  presetRegions: [
    { id: 'hyderabad', name: 'Hyderabad', center: { lat: 17.385, lng: 78.4867 }, bounds: { south: 17.2, west: 78.2, north: 17.6, east: 78.7 } },
    { id: 'mumbai', name: 'Mumbai', center: { lat: 19.076, lng: 72.8777 }, bounds: { south: 18.85, west: 72.75, north: 19.35, east: 73.05 } },
    { id: 'delhi', name: 'Delhi NCR', center: { lat: 28.6139, lng: 77.209 }, bounds: { south: 28.4, west: 76.85, north: 28.85, east: 77.35 } },
    { id: 'bangalore', name: 'Bangalore', center: { lat: 12.9716, lng: 77.5946 }, bounds: { south: 12.85, west: 77.45, north: 13.15, east: 77.75 } },
    { id: 'chennai', name: 'Chennai', center: { lat: 13.0827, lng: 80.2707 }, bounds: { south: 12.9, west: 80.15, north: 13.2, east: 80.35 } },
    { id: 'kolkata', name: 'Kolkata', center: { lat: 22.5726, lng: 88.3639 }, bounds: { south: 22.45, west: 88.25, north: 22.65, east: 88.45 } },
    { id: 'pune', name: 'Pune', center: { lat: 18.5204, lng: 73.8567 }, bounds: { south: 18.4, west: 73.75, north: 18.65, east: 74.0 } },
    { id: 'ahmedabad', name: 'Ahmedabad', center: { lat: 23.0225, lng: 72.5714 }, bounds: { south: 22.95, west: 72.5, north: 23.15, east: 72.7 } },
    { id: 'jaipur', name: 'Jaipur', center: { lat: 26.9124, lng: 75.7873 }, bounds: { south: 26.8, west: 75.7, north: 27.0, east: 75.9 } },
    { id: 'lucknow', name: 'Lucknow', center: { lat: 26.8467, lng: 80.9462 }, bounds: { south: 26.75, west: 80.85, north: 26.95, east: 81.05 } },
  ],

  // Feature Flags
  features: {
    offline: env('VITE_ENABLE_OFFLINE') === 'true',
    // Street View is not backed by real imagery yet; off by default so the
    // procedural placeholder viewer is not exposed in the UI.
    streetView: env('VITE_ENABLE_STREET_VIEW') === 'true',
    // Gates the live-traffic map overlay (congestion flow + incidents from the
    // traffic-service, rendered by components/map/TrafficLayer). Off by default;
    // set VITE_ENABLE_TRAFFIC=true to surface the Traffic layer toggle.
    traffic: env('VITE_ENABLE_TRAFFIC') === 'true',
    buildings3D: env('VITE_ENABLE_3D_BUILDINGS') === 'true',
    // Hillshade/terrain needs a raster-DEM tile source, which is not part of the
    // default self-hosted tile stack. Off by default so the Terrain toggle (which
    // would request a non-existent DEM endpoint) is hidden until a DEM is wired up.
    terrain: env('VITE_ENABLE_TERRAIN') === 'true',
    voiceNavigation: env('VITE_ENABLE_VOICE_NAVIGATION') === 'true',
    // Self-hosted TTS (consistent server-rendered Indian-English voice). When off,
    // navigation voice uses the browser Web Speech API.
    selfHostedTts: env('VITE_ENABLE_SELF_HOSTED_TTS') === 'true',
  },

  // Cache Configuration
  cache: {
    searchTTL: parseInt(env('VITE_CACHE_TTL_SEARCH') || '300000', 10),
    routeTTL: parseInt(env('VITE_CACHE_TTL_ROUTE') || '600000', 10),
    maxSize: parseInt(env('VITE_MAX_CACHE_SIZE') || '104857600', 10),
  },

  // Routing Configuration
  routing: {
    profiles: ['driving', 'cycling', 'walking', 'transit'] as const,
    defaultProfile: 'driving' as const,
    maxWaypoints: 10,
    maxAlternatives: 3,
  },

  // Search Configuration
  search: {
    debounceMs: 300,
    minQueryLength: 2,
    maxResults: 10,
    maxRecentSearches: 10,
  },

  // Offline Configuration
  offline: {
    maxRegions: 5,
    maxTilesPerRegion: 50000,
    tileExpirationDays: 30,
  },

  // Analytics
  analytics: {
    enabled: env('VITE_ANALYTICS_ENABLED') === 'true',
    id: env('VITE_ANALYTICS_ID') || '',
  },
} as const;

export type Config = typeof config;
export default config;
